import type React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertDialog, Button } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import {
    renderWithKeyShortcuts,
    ShortcutHint,
    VOID_CONFIRM_ACCEPT_SHORTCUT,
    VOID_CONFIRM_CANCEL_SHORTCUT,
} from '@/components/shortcuts';
import { formatGel } from '../../sellerMeta';
import { VoidConfirmScope } from './VoidConfirmScope';
import type { VoidConfirmState } from './useVoidConfirm';

const renderCancelButton = renderWithKeyShortcuts(
    'button',
    VOID_CONFIRM_CANCEL_SHORTCUT.combination,
);
const renderConfirmButton = renderWithKeyShortcuts(
    'button',
    VOID_CONFIRM_ACCEPT_SHORTCUT.combination,
);

type VoidConfirmDialogProps = VoidConfirmState & {
    lineCount: number;
    total: number;
};

/**
 * Confirms voiding the cart (bos-seller#57) — the Void button, the cart
 * header's clear-cart control, and `Ctrl+Delete` all open this same dialog
 * (`useCartPanel`). `isKeyboardDismissDisabled={false}` turns on the design
 * system's default-off Escape dismissal so `Esc` cancels. Every closing
 * route — Cancel, Esc, the close trigger — runs through `onOpenChange`, which
 * never clears the cart; only `confirm` does. Render-only: `useVoidConfirm`
 * owns the state and gives Confirm its initial focus through `confirmRef`.
 */
export const VoidConfirmDialog: React.FC<VoidConfirmDialogProps> = ({
    isOpen,
    onOpenChange,
    confirm,
    confirmRef,
    lineCount,
    total,
}) => {
    const { t } = useTranslation('pages');

    return (
        <>
            {isOpen ? <VoidConfirmScope /> : null}
            <AlertDialog.Backdrop
                isKeyboardDismissDisabled={false}
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                variant="blur"
            >
                <AlertDialog.Container placement="center" size="md">
                    <AlertDialog.Dialog
                        {...getNodeTestIdProp('seller-void-confirm')}
                    >
                        <AlertDialog.CloseTrigger />
                        <AlertDialog.Header>
                            <AlertDialog.Icon status="danger" />
                            <AlertDialog.Heading>
                                {t('seller.voidConfirm.title')}
                            </AlertDialog.Heading>
                        </AlertDialog.Header>
                        <AlertDialog.Body>
                            <p
                                {...getNodeTestIdProp(
                                    'seller-void-confirm-description',
                                )}
                            >
                                {t('seller.voidConfirm.description', {
                                    count: lineCount,
                                    total: formatGel(total),
                                })}
                            </p>
                        </AlertDialog.Body>
                        <AlertDialog.Footer>
                            <Button
                                {...getNodeTestIdProp(
                                    'seller-void-confirm-cancel',
                                )}
                                onPress={() => onOpenChange(false)}
                                render={renderCancelButton}
                                variant="tertiary"
                            >
                                {t('seller.voidConfirm.cancel')}
                                <ShortcutHint
                                    placement="control"
                                    shortcut={VOID_CONFIRM_CANCEL_SHORTCUT}
                                />
                            </Button>
                            <Button
                                {...getNodeTestIdProp(
                                    'seller-void-confirm-confirm',
                                )}
                                onPress={confirm}
                                ref={confirmRef}
                                render={renderConfirmButton}
                                variant="danger"
                            >
                                {t('seller.voidConfirm.confirm')}
                                <ShortcutHint
                                    placement="control"
                                    shortcut={VOID_CONFIRM_ACCEPT_SHORTCUT}
                                />
                            </Button>
                        </AlertDialog.Footer>
                    </AlertDialog.Dialog>
                </AlertDialog.Container>
            </AlertDialog.Backdrop>
        </>
    );
};
